import { useCallback, useEffect, useState } from 'react';
import { ZuoraPaymentMethod, ZuoraSignature } from '@kimuno/api-sdk/lib/services/billing';
import config from 'config';

import {
  ZuoraPaymentPageClientSideErrorHandler,
  ZuoraPaymentPageResponse,
  ZuoraPaymentPageResponseHandler
} from '../types/ZuoraPayments';
import { isEveryPayPalPaymentObjectDefined, isZuoraPaymentObjectDefined } from './payment';
import { notifyBugsnagHandledError } from './bugsnagClient';
import services from '../services';
import { UserPaymentMethodType } from '../types/User';

interface usePaymentSetupProps {
  selectedMethod: string;
  onResponseHandler: (paymentMethod: ZuoraPaymentMethod, response: ZuoraPaymentPageResponse) => void;
  onClientSideErrorHandler?: ZuoraPaymentPageClientSideErrorHandler
  zuoraHostedPagesSource: string
}

const usePaymentSetup = ({
  selectedMethod,
  onResponseHandler,
  onClientSideErrorHandler,
  zuoraHostedPagesSource
}: usePaymentSetupProps) => {
  const [paypalToken, setPaypalToken] = useState('');
  const [hasCreditCardErrors, setHasCreditCardErrors] = useState(false);

  const setupPaypal = useCallback(() => {
    if (!isEveryPayPalPaymentObjectDefined()) {

      const error = `Not every PayPal payment object is defined. 
          !!window.braintree.client: ${!!window.braintree?.client}, 
          !!window.braintree.paypalCheckout: ${!!window.braintree?.paypalCheckout},
          !!window.paypal.Button: ${!!window.paypal?.Button},`.replace(/(\r\n|\n|\r)/gm, '').replace(/\s{2,}/g, ' ');

      notifyBugsnagHandledError(new Error(error));
      return;
    }

    services.billing.getBraintreeSignature()
      .then(data => setPaypalToken(data.token));
  }, []);

  const isZuoraMethod = (method: string): method is ZuoraPaymentMethod => {
    return method === UserPaymentMethodType.CREDIT_CARD || method === UserPaymentMethodType.SEPA;
  };

  const setupZuora = useCallback((paymentMethod: string) => {
    if (!isZuoraPaymentObjectDefined()) {
      const error = `Zuora payment object is not defined. !!window.Z: ${!!window.Z}`;
      notifyBugsnagHandledError(new Error(error));

      return;
    }

    const renderPaymentPage = (
      signature: ZuoraSignature,
      paymentMethod: ZuoraPaymentMethod,
      onResponseHandler: ZuoraPaymentPageResponseHandler
    ) => {

      const onClientSideErrorHandlerProxy = (key: string, code: string, message: string) => {
        // Send the error to the UI
        window.Z.sendErrorMessageToHpm(key, message);
        // Invoke our custom client-side-error-handler
        if (onClientSideErrorHandler) onClientSideErrorHandler(key, code, message);
        setHasCreditCardErrors(true);
      };

      const params = {
        locale: 'de_DE',
        style: 'inline',
        submitEnabled: 'true',
        url: config.zuoraHostedPagesUri,
        id: signature.pageId,
        key: signature.key,
        signature: signature.signature,
        token: signature.token,
        tenantId: signature.tenantId,
      };

      window.Z.renderWithErrorHandler(
        params,
        {},
        (response: ZuoraPaymentPageResponse) => onResponseHandler(paymentMethod, response),
        onClientSideErrorHandlerProxy
      );
    };

    services.billing.getSignature(paymentMethod as ZuoraPaymentMethod, zuoraHostedPagesSource)
      .then((signature) => renderPaymentPage(signature, paymentMethod as ZuoraPaymentMethod, onResponseHandler));
  }, [onResponseHandler, zuoraHostedPagesSource]);

  useEffect(() => {
    if (selectedMethod === UserPaymentMethodType.PAYPAL) setupPaypal();
    else if (isZuoraMethod(selectedMethod)) {
      setupZuora(selectedMethod);
    }
  }, [selectedMethod]);

  return { paypalToken, hasCreditCardErrors };
};

export default usePaymentSetup;
